import { useState, useRef } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import SignatureCanvas from 'react-signature-canvas';
import Button from '../common/Button';

/**
 * Signature Creation/Update Modal
 * Allows users to draw their signature using a canvas
 */
export default function SignatureModal({ isOpen, onClose, onSave, existingSignature = null }) {
  const sigPadRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleClear = () => {
    sigPadRef.current?.clear();
  };

  const handleSave = async () => {
    if (sigPadRef.current?.isEmpty()) {
      alert('Please provide a signature');
      return;
    }

    try {
      setIsSaving(true);
      const signatureDataUrl = sigPadRef.current.toDataURL('image/png');
      await onSave(signatureDataUrl);
      onClose();
    } catch (error) {
      console.error('Error saving signature:', error);
      alert('Failed to save signature. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      if (confirm('Discard changes to signature?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {existingSignature ? 'Update Your Signature' : 'Create Your Signature'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Canvas Container */}
            <div className="border-2 border-gray-300 rounded-lg bg-white overflow-hidden">
              <SignatureCanvas
                ref={sigPadRef}
                canvasProps={{
                  className: 'w-full h-48 cursor-crosshair',
                  style: { touchAction: 'none' }
                }}
                backgroundColor="rgb(255, 255, 255)"
                penColor="rgb(0, 0, 0)"
              />
            </div>

            {/* Help Text */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Draw your signature above using your mouse or touch screen
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClear}
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Your signature will be automatically applied to contracts you create. 
                Make sure it's clear and matches your official signature.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isSaving}
            >
              Save Signature
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}